import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { IoArrowBackOutline, IoOpenOutline } from "react-icons/io5";
import { getPage } from "../../../api/pagesApi";
import { listSections } from "../../../api/sectionsApi";
import { getApiErrorMessage } from "../../../api/unwrapApiResponse";
import { isServicePageSlug } from "../../../config/cmsServicePages";
import { isIndustryPageSlug } from "../../../config/cmsIndustryPages";
import DashboardPanel from "../../../components/dashboard/DashboardPanel";
import ErrorState from "../../../components/dashboard/ErrorState";
import LoadingState from "../../../components/dashboard/LoadingState";
import { MEDIA_SECTION_TYPES } from "../../../utils/sectionTypes";
import DynamicFieldsEditor from "../fields/DynamicFieldsEditor";
import FooterSocialsManager from "../footerSocials/FooterSocialsManager";
import MediaManager from "../media/MediaManager";

export default function SectionEditor() {
  const { pageId, sectionId } = useParams();
  const [page, setPage] = useState(null);
  const [section, setSection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadSection = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [pageData, sections] = await Promise.all([
        getPage(pageId),
        listSections(pageId),
      ]);
      setPage(pageData);
      const list = Array.isArray(sections) ? sections : [];
      const match = list.find((s) => String(s.id) === String(sectionId));
      if (!match) {
        setError("Section not found.");
        setSection(null);
        return;
      }
      setSection(match);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [pageId, sectionId]);

  useEffect(() => {
    loadSection();
  }, [loadSection]);

  if (loading) return <LoadingState message="Loading section…" />;
  if (error) return <ErrorState message={error} onRetry={loadSection} />;
  if (!section) return <ErrorState message="Section not found." />;

  const showMedia = MEDIA_SECTION_TYPES.has(
    (section.sectionType || "").toLowerCase()
  );
  const isArticlesSection =
    (section.sectionType || "").toLowerCase() === "articles";
  const isProjectsSection =
    (section.sectionType || "").toLowerCase() === "projects";
  const isFooterSection =
    (section.key || "").toLowerCase() === "footer" ||
    (section.sectionType || "").toLowerCase() === "footer";
  const backTo = isServicePageSlug(page?.slug)
    ? "/cmsadmin/dashboard/services"
    : isIndustryPageSlug(page?.slug)
      ? "/cmsadmin/dashboard/industries"
      : `/cmsadmin/dashboard/pages/${pageId}/sections`;
  const backLabel = isServicePageSlug(page?.slug)
    ? "Back to service pages"
    : isIndustryPageSlug(page?.slug)
      ? "Back to industry pages"
      : "Back to sections";

  return (
    <div className="space-y-6">
      <Link
        to={backTo}
        className="inline-flex items-center gap-2 text-sm text-slate-500 transition hover:text-slate-900"
      >
        <IoArrowBackOutline className="h-4 w-4" />
        {backLabel}
      </Link>

      <DashboardPanel
        noPadding
        bodyClassName=""
        title={section.name}
        description={`${page?.name || "Page"} · ${section.key} · ${section.sectionType}`}
        badge={
          <span
            className={`rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${
              section.isPublished
                ? "bg-emerald-100 text-emerald-800"
                : "bg-amber-100 text-amber-800"
            }`}
          >
            {section.isPublished ? "Published" : "Draft"}
          </span>
        }
      >
        <div className="border-t border-slate-100 px-5 py-3 text-xs text-slate-500">
          {isFooterSection
            ? "Edit footer copy and link columns below. Manage social links in the dedicated Socials panel."
            : "Edit the grouped fields below. Changes are saved per section using the save button at the bottom."}
        </div>
      </DashboardPanel>

      {isFooterSection ? (
        <DashboardPanel
          title="Social links"
          description="Add, delete, or change icons for footer socials"
        >
          <FooterSocialsManager
            embedded
            sectionId={section.id}
            pageId={page?.id}
            pageName={page?.name || ""}
            sectionName={section.name || ""}
          />
        </DashboardPanel>
      ) : null}

      {isArticlesSection ? (
        <DashboardPanel
          title="Articles"
          description="Manage insight articles from the dedicated articles manager"
        >
          <p className="text-sm text-slate-600">
            Articles use a dedicated editor with list view, add/edit forms, and
            page settings in one place.
          </p>
          <Link
            to="/cmsadmin/dashboard/articles"
            className="mt-4 inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-800"
          >
            <IoOpenOutline className="h-4 w-4" />
            Open Articles manager
          </Link>
        </DashboardPanel>
      ) : isProjectsSection ? (
        <DashboardPanel
          title="Work projects"
          description="Manage case studies from the dedicated projects manager"
        >
          <p className="text-sm text-slate-600">
            Work projects are managed separately — create case studies, edit
            detail content, and choose featured projects.
          </p>
          <Link
            to="/cmsadmin/dashboard/projects"
            className="mt-4 inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-800"
          >
            <IoOpenOutline className="h-4 w-4" />
            Open Work projects manager
          </Link>
        </DashboardPanel>
      ) : (
        <DynamicFieldsEditor
          sectionId={sectionId}
          excludeKeyPrefix={isFooterSection ? "social_" : ""}
        />
      )}

      {showMedia ? (
        <DashboardPanel
          title="Section media"
          description="Background images, galleries, and other uploads for this section"
        >
          <MediaManager sectionId={sectionId} sectionType={section.sectionType} />
        </DashboardPanel>
      ) : null}
    </div>
  );
}
